import React from 'react';
import { Button } from '@mui/material';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import { notify } from '../../notifications';
import { ACTION_BUTTON_SX } from '../reports/summary/summaryStyles';

interface CashReportProps {
  tableData: any[];
  selectedUser: { username: string; pharmacyNum: string } | null;
  startDate: string;
  endDate: string;
  employeeName: string | null;
  /** Принудительно отключить кнопку (например, пока данные за выбранный период перезагружаются) */
  disabled?: boolean;
  /** Вызывается после того, как файл сформирован (запись в журнал) */
  onGenerated?: () => void;
}

const CashReport: React.FC<CashReportProps> = ({ tableData, selectedUser, startDate, endDate, employeeName, disabled, onGenerated }) => {
  const getMonthName = (date: string): string => {
    const months = [
      'январь', 'февраль', 'март', 'апрель', 'май', 'июнь',
      'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'
    ];
    const monthIndex = new Date(date).getMonth();
    return months[monthIndex];
  };

  const getYear = (date: string): string => {
    return new Date(date).getFullYear().toString();
  };

  const generateReport = async () => {
    try {
      // шаблон
      const response = await fetch('/templates/cash_report_template.docx');
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();

      const rows = tableData.map((row, index) => ({
        num: index + 1,
        date: row.date,
        cash_balance_start_of_day: row.cash_balance_start_of_day || 0,
        cash: row.cash || 0,
        cash_collection: row.cash_collection || 0,
        funds_transfer_to_office: row.funds_transfer_to_office || 0,
        other_expenses: row.other_expenses || 0,
        cash_balance_end_of_day: row.cash_balance_end_of_day || 0
      }));

      // итоги
      const totals = rows.reduce((acc, row) => ({
        totalCash_balance_start_of_day: acc.totalCash_balance_start_of_day + (parseFloat(row.cash_balance_start_of_day) || 0),
        totalCash: acc.totalCash + (parseFloat(row.cash) || 0),
        totalCash_collection: acc.totalCash_collection + (parseFloat(row.cash_collection) || 0),
        totalCashTransfer: acc.totalCashTransfer + (parseFloat(row.funds_transfer_to_office) || 0),
        totalOtherExpense: acc.totalOtherExpense + (parseFloat(row.other_expenses) || 0),
        totalCash_balance_end_of_day: acc.totalCash_balance_end_of_day + (parseFloat(row.cash_balance_end_of_day) || 0)
      }), {
        totalCash_balance_start_of_day: 0,
        totalCash: 0,
        totalCash_collection: 0,
        totalCashTransfer: 0,
        totalOtherExpense: 0,
        totalCash_balance_end_of_day: 0
      });

      const data = {
        pharmacyNum: selectedUser?.pharmacyNum || '',
        currentMonth: getMonthName(startDate),
        currentYear: getYear(startDate),
        period: `${startDate} - ${endDate}`,
        employeeName: employeeName || '',
        rows,
        ...totals
      };

      // экземпляр docxtemplater
      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, {
        paragraphLoop: true,
        linebreaks: true
      });

      doc.render(data);

      const out = doc.getZip().generate({
        type: 'blob',
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });

      const fileName = `Отчет о движении наличных денежных средств аптека №${selectedUser?.pharmacyNum} за ${startDate} - ${endDate}.docx`;
      saveAs(out, fileName);
      if (onGenerated) onGenerated();

    } catch (error) {
      console.error('Ошибка при генерации отчета:', error);
      notify.error('Не удалось сформировать отчет по кассе');
    }
  };

  return (
    <Button
      variant="outlined"
      onClick={generateReport}
      disabled={disabled || !tableData.length}
      sx={ACTION_BUTTON_SX}
    >
      Касса
    </Button>
  );
};

export default CashReport;